/* eslint-disable @typescript-eslint/no-var-requires */
import CashFlowReport from "./CashFlowReport";
import FinancesReport from "./FinancesReport";
import Download from "../../../assets/images/download.png";
import moment from "moment";
import { saveAs } from "file-saver";
import { Office } from "../../../models/office.model";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useState } from "react";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { CircularProgress } from "@mui/material";
import { FinanceReportKeys } from "../../../utils/constants";
import { FinanceBilledTo } from "../../../models/finance.model";
import FinancesReportPDF from "./FinancesReportPDF";
import CashFlowReportPDF from "./CashFlowReportPDF";
import { logger } from "../../../utils/logger";
import { AppDispatch, RootState } from "../../../redux/store";
import { getOfficeAnnualPlanReportCsv, getOfficeFinanceReportCsv, getOfficeReportForMTD } from "../../../redux/slices/FinanceSlice";
import { getReportDateRange } from "../../../utils";
import { downloadFile } from "../../../utils/fileDownloads";
import { getPdfOptions } from "../../../utils/getPdfoption";
import { loadSecureZip } from "../../../utils/loadSecureZip";

const html2pdf = require("html2pdf.js");
const JSZip = require("jszip");
const log = logger.getLogger("ReportExportError");

const ReportingTab = ({
  selectedMonth,
  selectedOffice,
  monthName,
  monthOptions,
  changeReportTabMonth,
}: {
  monthOptions: any;
  changeReportTabMonth: any;
  selectedMonth: string;
  selectedOffice: Office | null;
  monthName: any;
}) => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const { financeReportTabData } = useSelector((state: RootState) => state.finance);

  const reportMonth = selectedMonth || moment().subtract(1, "month").format("MMMM YYYY");

  const downloadAsMTD = async (element: HTMLElement | null, SummaryPDF: string, monthYearKey: string) => {
  
    try {
      const myresponse: any = await dispatch(
        getOfficeReportForMTD({
          officeId: selectedOffice?.officeId ?? "",
          monthYearKey: monthYearKey,
        })
      );

      const fileUrl = myresponse?.payload?.downloadUrl;
      if (!fileUrl) {
        throw new Error("No download URL received");
      }

      const res = await fetch(fileUrl);
      if (!res.ok) {
        throw new Error("Download failed with status " + res.status);
      }

      const blob = await res.blob();
      const existingZip = await loadSecureZip(blob);
      element?.classList.remove("hidden");

      const opt = getPdfOptions(myresponse?.payload?.fileName);
      const pdfBuffer = await html2pdf().set(opt).from(element).outputPdf("arraybuffer");

      existingZip.file(`${SummaryPDF}.pdf`, pdfBuffer);
      existingZip.generateAsync({ type: "blob" }).then((newBlob: any) => {
        downloadFile(newBlob, SummaryPDF + ".zip");
        setLoading(false);
      });
    } catch (error) {
      toast?.errorToast("Sorry, your request could not be processed. Please contact Docnote Support team.");
      setLoading(false);
    }
  };

  const downloadInvoiceAsPdf = async () => {
    try {
      setLoading(true);
      const isMTD = selectedMonth.includes("MTD");
      const splitMonth = reportMonth.split(" ");
      const month = monthName.indexOf(splitMonth[0]);
      const year = splitMonth[1];
      const { firstDay, lastDay } = getReportDateRange(month, reportMonth, isMTD);
      const monthYearKey = `${splitMonth[0]}_${splitMonth[1]}`;
      let accountingCsvData = "";
      let annualPlanCsvData = "";
      const element = document.getElementById("finance-report-container");
      const fileMonth = `${moment([year, month]).format("MMMM")}${moment([year, month]).format("YYYY")}`;
      const officeName = selectedOffice?.name ? selectedOffice?.name?.split(" ").join("") : "";
      const SummaryPDF = `${officeName}Summary${fileMonth}`;
      const isAboveJune2025 = year > "2025" || (year === "2025" && month > monthName.indexOf("June"));
      if (selectedOffice?.officeId && !isAboveJune2025) {
        const requestParams = {
          officeId: selectedOffice.officeId,
          startDate: new Date(firstDay),
          endDate: new Date(lastDay),
        };
        const accountingReportResponse: any = await dispatch(getOfficeFinanceReportCsv(requestParams));
        const annualPlanReportResponse: any = await dispatch(getOfficeAnnualPlanReportCsv(requestParams));

        const accountingData = accountingReportResponse?.payload?.toString().split(/\n/);
        if (accountingData?.length) {
          const data = accountingReportResponse?.payload?.toString().split(/\n/);
          const headers = data[0];
          const csvRows = [];
          csvRows.push(headers);

          data.slice(1).map((item: string) => {
            csvRows.push(item);
            return true;
          });
          accountingCsvData = csvRows.join("\n");
        }

        const annualPlanData = annualPlanReportResponse?.payload?.toString().split(/\n/);
        if (annualPlanData?.length) {
          const data = annualPlanReportResponse?.payload?.toString().split(/\n/);
          const headers = data[0];
          const csvRows = [];
          csvRows.push(headers);
          data.slice(1).map((item: string) => {
            csvRows.push(item);
            return true;
          });
          annualPlanCsvData = csvRows.join("\n");
        }

        const AccountingCSV = `${officeName}AccountingCSV${fileMonth}.csv`;
        const AnnualPlanCSV = `${officeName}AnnualPlanCSV${fileMonth}.csv`;
        const zipName = `${officeName}MonthlyReport${fileMonth}.zip`;
        element?.classList.remove("hidden");
        const opt = getPdfOptions(SummaryPDF);
        const zip = new JSZip();
        zip.file(`${SummaryPDF}.pdf`, html2pdf().set(opt).from(element).outputPdf("arraybuffer"));
        if (accountingCsvData) {
          zip.file(`${AccountingCSV}`, new Blob([accountingCsvData], { type: "text/csv" }));
        }
        if (annualPlanCsvData) {
          zip.file(`${AnnualPlanCSV}`, new Blob([annualPlanCsvData], { type: "text/csv" }));
        }
        zip.generateAsync({ type: "blob" }).then((blob: any) => {
          saveAs(blob, `${zipName}`);
        });
        setLoading(false);
      } else {
        downloadAsMTD(element, SummaryPDF, monthYearKey);
      }
    } catch (err) {
      setLoading(false);
      toast?.openToast("Download report failed. Try again!");
      log.warn(JSON.stringify(err));
    }
  };

  useEffect(() => {
    if (financeReportTabData) {
      const reportData: any = financeReportTabData;
      const length = FinanceReportKeys.filter((item: string) => reportData[item] !== undefined && reportData[item] > 0).length;
      const length1 = financeReportTabData?.billedTo?.filter((item: FinanceBilledTo) => item.amount > 0).length;

      setShowDownloadButton(!length && !length1 ? false : true);
    }
  }, [financeReportTabData]);

  return (
    <div className="relative">
      <div className="mb-5 flex items-center">
        {loading ? (
          <CircularProgress size={22} className="ml-5" />
        ) : showDownloadButton ? (
          <img src={Download} alt="Download Icon" className="w-6 h-6 cursor-pointer" onClick={downloadInvoiceAsPdf} />
        ) : null}
      </div>
      <div className="grid lg:grid-cols-2 gap-8 mb-10">
        <FinancesReport />
        <CashFlowReport />
      </div>
      <div className="opacity-0">
        <div id="finance-report-container" className="lg:grid-cols-1 hidden">
          <FinancesReportPDF isPdf={true} selectedMonth={reportMonth} />
          <CashFlowReportPDF isPdf={true} selectedMonth={reportMonth} />
        </div>
      </div>
    </div>
  );
};

export default ReportingTab;
